import {
  CaseArtifactTypes,
  ChargeSessionCodeProvenance,
  ChargeSessionCodeType,
  InvoiceTypes
} from '../../enums';
import { emptyInvoice } from '../charges/empty-invoice';
import * as evidenceConverters from '../artifacts/evidence-converter';
import { simplifiedInvoices } from '../charges/charge-converters';

// ------------------------------------------
// Base data structures
// ------------------------------------------
const BASE_CODE: CMxCommonApp.ChargeSessionCode = {
  codeType: '' as CMxCommonApp.ChargeSessionCodeType,
  provenance: '' as CMxCommonApp.ChargeSessionCodeProvenance,
  code: '',
  units: 1,
  hash: '',
  unitable: true,
  editable: false,
  codeIssues: [],
  evidence: [],
  description: '',
  codeProvenance: []
};

const BASE_CHARGE_SESSION: CMxCommonApp.SimplifiedTransaction = {
  type: '',
  guid: '',
  department_code: '',
  description: '',
  codes: []
};

const BASE_INVOICE: CMxCommonApp.SimplifiedInvoice = {
  ...emptyInvoice,
  shared_codes: {
    diagnoses: [],
    modifiers: []
  },
  transactions: []
};

const OWN_META: CMxArtifacts.ArtifactMetaData = {
  imported: false,
  ownerCaseId: null,
  description: 'These codes originated in your system'
};

const LINKED_META: CMxArtifacts.ArtifactMetaData = {
  imported: true,
  ownerCaseId: null,
  description: 'These codes come from a different case'
};

// ------------------------------------------
// Helper functions
// ------------------------------------------

const convertArtifactToInovice = (
  artifact: CMxAPI.CaseArtifacts
): CMxCommonApp.SimplifiedInvoice => {
  const invoice = { ...BASE_INVOICE, type: artifact.type };

  // codes
  const procedureCodes =
    artifact.procedures?.codes?.map(procedure => {
      const hash = `${artifact.artifact_id}__${procedure.procedure_code}`;
      const chargeSessionCode = {
        ...BASE_CODE,
        codeType: ChargeSessionCodeType.PROCEDURE,
        provenance: ChargeSessionCodeProvenance.CPT,
        code: procedure.procedure_code,
        description: procedure.procedure_name,
        evidence: evidenceConverters.convertCPT(procedure as any, hash, true)
      };
      return chargeSessionCode;
    }) || [];

  const modifierCodes =
    artifact.modifiers?.codes?.map(modifier => {
      const chargeSessionCode = {
        ...BASE_CODE,
        codeType: ChargeSessionCodeType.MODIFIER,
        provenance: ChargeSessionCodeProvenance.MODIFIER,
        code: modifier.modifier_code,
        description: modifier.modifier_code,
        evidence: [] // evidence not supported on modifiers for now.
      };
      return chargeSessionCode;
    }) || [];

  const dxCodes =
    artifact.diagnoses?.codes?.map(diag => {
      const id = `${artifact.artifact_id}${diag.diagnosis_code}`.replace(
        /(\.|\:)/gi,
        '__'
      );
      const chargeSessionCode = {
        ...BASE_CODE,
        codeType: ChargeSessionCodeType.DIAGNOSIS,
        provenance: ChargeSessionCodeProvenance.ICD,
        code: diag.diagnosis_code,
        description: diag.diagnosis_code,
        evidence: evidenceConverters.convertDX(diag as any, id, true)
      };
      return chargeSessionCode;
    }) || [];
  // for the prediction artifact, we only have one transaction.  c
  invoice.transactions = [
    {
      ...BASE_CHARGE_SESSION,
      codes: [...procedureCodes, ...modifierCodes, ...dxCodes]
    }
  ];
  return invoice;
};

// ------------------------------------------
// Public API
// ------------------------------------------

/**
 * Creates an invoice from an artifact.  This is a UI only construct to allow a prediction
 * to be displayed in the UI when we only show invoices at this point.
 * @param artifacts
 * @returns CMxCommonApp.SimplifiedInvoice[]
 */
export const artifactToSimplifiedInvoice = (
  artifacts: CMxAPI.CaseArtifacts[]
): CMxCommonApp.SimplifiedInvoice[] => {
  return artifacts.map(convertArtifactToInovice);
};

/**
 * A cases codes may be submitted with a different case
 */
export const handleImportedCases = (
  originalInvoices: CMxCommonApp.SimplifiedInvoice[],
  submitterProcess: CMxAPI.CaseRun,
  submitterSet: Set<InvoiceTypes>,
  originalSet: Set<InvoiceTypes>
): CMxCommonApp.SimplifiedInvoice[] => {
  // grab only the invoices that we want from the submitter process
  const submitterImportedInvoices = simplifiedInvoices(
    submitterProcess
  )?.filter(invoice => submitterSet.has(invoice.type as InvoiceTypes));
  const importedInvoiceGuids = new Set(
    submitterImportedInvoices.map(invoice => invoice.guid)
  );
  // add in metadata for the new
  const meta = {
    ...LINKED_META,
    ownerCaseId: submitterProcess.case_id,
    description: `${
      submitterProcess.exam_description
    } - Acc #: ${submitterProcess.primary_clinical_identifier?.number ??
      'N/A'} (${submitterProcess.primary_clinical_identifier?.issuer ?? 'N/A'})`
  };
  const importedInvoices = submitterImportedInvoices.map(invoice => ({
    ...invoice,
    meta: { ...meta }
  }));

  // make sure we only have a clean set of the old invoices
  const onlyOwnInvoices = originalInvoices.filter(
    invoice => !importedInvoiceGuids.has(invoice.guid)
  );

  if (importedInvoices.length) {
    onlyOwnInvoices
      .filter(invoice => originalSet.has(invoice.type as InvoiceTypes))
      .forEach(invoice => (invoice.meta = { ...OWN_META }));
  }

  return [...importedInvoices, ...onlyOwnInvoices];
};
